import { type CSSProperties, type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DistributionPoint } from "../SalesProductAnalyticsTabsDashboard.data";
import {
  buildSalesByChannelChartModel,
  formatPkrCompact,
  formatPkrDetailed,
  type BuildSalesByChannelChartModelOptions,
  type SalesByChannelChartRow,
} from "../../utils/SalesByChannelChartModel";
import {
  localizeChartDigits,
  mirrorChartMargin,
  rtlChartProps,
} from "../SalesProductShared";
import { calculateBarWidthPct } from "src/utils/chartBarScale";
import { useLanguage } from "src/hooks/UseLanguage";
import "./SalesByChannelChart.scss";

const BAR_PALETTE = [
  "#2563eb",
  "#35cdbb",
  "#a855f7",
  "#60a5fa",
  "#10b981",
  "#f59e0b",
  "#2563eb",
  "#2dd4bf",
  "#a855f7",
  "#60a5fa",
  "#0ea5e9",
  "#9333ea",
  "#16a34a",
  "#ea580c",
  "#64748b",
  "#475569",
] as const;

const channelBarFill = (index: number) => BAR_PALETTE[index % BAR_PALETTE.length];
const TREND_CHART_MARGIN = { top: 8, right: 80, left: 0, bottom: 8 };

export type SalesByChannelChartSummaryLabels = {
  /** Left column heading (e.g. "Total revenue") */
  totalLabel: string;
  /** Small line under total amount */
  totalHint: string;
  /** Right column heading (e.g. "Top channel") */
  topLabel: string;
  /** Small line under top name, e.g. "42% of total revenue" */
  topHint: string;
  /** Tooltip primary amount row label */
  tooltipMetricLabel: string;
  /** Tooltip rank line after share %, e.g. "of total revenue" */
  tooltipShareQualifier: string;
  /** Accessible description for the figure */
  ariaLabel: string;
  /** `aria-label` for summary strip */
  summaryAriaLabel: string;
};

const DEFAULT_SUMMARY_LABELS: SalesByChannelChartSummaryLabels = {
  totalLabel: "Total revenue",
  totalHint: "",
  topLabel: "Top channel",
  topHint: "",
  tooltipMetricLabel: "Sales revenue",
  tooltipShareQualifier: "of total revenue",
  ariaLabel: "Sales revenue by channel, horizontal bars sorted by revenue",
  summaryAriaLabel: "Channel revenue summary",
};

type SalesByChannelTooltipProps = {
  active?: boolean;
  payload?: Array<{ payload?: unknown }>;
  tooltipMetricLabel: string;
  tooltipShareQualifier: string;
  valueKind: "currency" | "number";
};

const SalesByChannelTooltip: FC<SalesByChannelTooltipProps> = ({
  active,
  payload,
  tooltipMetricLabel,
  tooltipShareQualifier,
  valueKind,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0].payload as SalesByChannelChartRow | undefined;
  if (!row) {
    return null;
  }
  const { currencyLabel } = row;
  const amount = row.displayAmount ?? row.value;
  const sharePct = localizeChartDigits(`${row.shareOfTotal}%`, isRTL);

  return (
    <div className="sales-by-channel-chart__tooltip">
      <div className="sales-by-channel-chart__tooltip-accent" aria-hidden />
      <div className="sales-by-channel-chart__tooltip-inner">
        <div className="sales-by-channel-chart__tooltip-rank">
          {t("salesProduct.shared.channelChart.rank", { rank: localizeChartDigits(String(row.rank), isRTL) })}
          {" · "}
          {sharePct} {tooltipShareQualifier}
        </div>
        <div className="sales-by-channel-chart__tooltip-title">{row.label}</div>
        <div className="sales-by-channel-chart__tooltip-row">
          <span className="sales-by-channel-chart__tooltip-key">{tooltipMetricLabel}</span>
          <span className="sales-by-channel-chart__tooltip-val">
            {valueKind === "currency"
              ? row.formattedValue ?? localizeChartDigits(formatPkrDetailed(amount, currencyLabel), isRTL)
              : localizeChartDigits(Math.round(amount).toLocaleString(), isRTL)}
          </span>
        </div>
        <div className="sales-by-channel-chart__tooltip-row">
          <span className="sales-by-channel-chart__tooltip-key">{t("salesProduct.shared.channelChart.mixShare")}</span>
          <span className="sales-by-channel-chart__tooltip-val">{sharePct}</span>
        </div>
      </div>
    </div>
  );
};

export type SalesByChannelChartProps = {
  channels: DistributionPoint[];
  currencyLabel?: string;
  valueKind?: "currency" | "number";
  /** Shown when there are no rows */
  emptyMessage?: string;
  modelOptions?: BuildSalesByChannelChartModelOptions;
  /** Override summary + tooltip + a11y copy (merged with defaults). */
  summaryLabels?: Partial<SalesByChannelChartSummaryLabels>;
  showSummary?: boolean;
};

/**
 * Horizontal distribution bars (Recharts): channel revenue by default; same layout for tax / P&amp;L lines.
 */
export const SalesByChannelChart: FC<SalesByChannelChartProps> = ({
  channels,
  currencyLabel = "PKR",
  valueKind = "currency",
  emptyMessage = "No channel revenue for this range. Try different dates or confirm Shopify channel data.",
  modelOptions,
  summaryLabels: summaryLabelsPartial,
  showSummary = true,
}) => {
  const { isRTL } = useLanguage();
  const chartRtl = rtlChartProps(isRTL);
  const summaryLabels = useMemo(
    () => ({ ...DEFAULT_SUMMARY_LABELS, ...summaryLabelsPartial }),
    [summaryLabelsPartial],
  );

  const model = useMemo(
    () => buildSalesByChannelChartModel(channels, currencyLabel, modelOptions),
    [channels, currencyLabel, modelOptions],
  );

  const plotStyle = useMemo(
    () =>
      ({
        "--sales-by-channel-rows": String(Math.max(model.rows.length, 1)),
      }) as CSSProperties,
    [model.rows.length],
  );

  const yAxisLabelWidth = useMemo(() => {
    const longest = model.rows.reduce((max, r) => Math.max(max, r.label.length), 0);
    return Math.min(118, Math.max(64, Math.ceil(longest * 6.75) + 16));
  }, [model.rows]);
  const formatChartValue = (value: number) =>
    localizeChartDigits(
      valueKind === "currency"
        ? formatPkrCompact(value, currencyLabel)
        : Math.round(value).toLocaleString(),
      isRTL,
    );

  const topHintResolved =
    summaryLabels.topHint ||
    `${localizeChartDigits(`${model.top1SharePct}%`, isRTL)} ${summaryLabels.tooltipShareQualifier}`;

  const totalHintResolved =
    summaryLabels.totalHint ||
    `${model.channelCount} active channel${model.channelCount === 1 ? "" : "s"}`;

  if (model.channelCount === 0) {
    return (
      <div className="sales-by-channel-chart">
        <p className="sales-by-channel-chart__empty">{emptyMessage}</p>
      </div>
    );
  }

  if (!showSummary) {
    const rowValues = model.rows.map(row => row.value);
    const maxValue = Math.max(...rowValues, 1);
    const minValue = Math.min(...rowValues.filter(v => v > 0), 1);
    return (
      <div className="sales-by-channel-chart sales-by-channel-chart--bars">
        <figure
          className="sales-by-channel-chart__bars-plot"
          style={plotStyle}
          aria-label={summaryLabels.ariaLabel}
        >
          <div className="sales-by-channel-chart__bars-list">
            {model.rows.map((row, index) => {
              const widthPct = calculateBarWidthPct(row.value, maxValue, minValue);
              return (
                <div className="sales-by-channel-chart__bar-row" key={`${row.label}-${index}`}>
                  <div className="sales-by-channel-chart__bar-name">{row.label}</div>
                  <div className="sales-by-channel-chart__bar-track">
                    <span
                      className="sales-by-channel-chart__bar-fill"
                      style={{
                        width: `${widthPct}%`,
                        backgroundColor: channelBarFill(Math.max(0, row.rank - 1)),
                      }}
                    />
                  </div>
                  <div className="sales-by-channel-chart__bar-value">
                    {valueKind === "currency" && row.formattedValue
                      ? row.formattedValue
                      : formatChartValue(row.value)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="sales-by-channel-chart__bars-axis" aria-hidden>
            <span>{formatChartValue(0)}</span>
            <span>{formatChartValue(maxValue / 2)}</span>
            <span>{formatChartValue(maxValue)}</span>
          </div>
        </figure>
      </div>
    );
  }

  return (
    <div className="sales-by-channel-chart">
      {showSummary ? (
      <section
        className="sales-by-channel-chart__summary"
        aria-label={summaryLabels.summaryAriaLabel}
      >
        <div className="sales-by-channel-chart__summary-item">
          <span className="sales-by-channel-chart__summary-label">{summaryLabels.totalLabel}</span>
          <span className="sales-by-channel-chart__summary-value">
            {localizeChartDigits(
              valueKind === "currency"
                ? formatPkrDetailed(model.totalRevenue, currencyLabel)
                : Math.round(model.totalRevenue).toLocaleString(),
              isRTL,
            )}
          </span>
          <span className="sales-by-channel-chart__summary-hint">{totalHintResolved}</span>
        </div>
        <div className="sales-by-channel-chart__summary-item">
          <span className="sales-by-channel-chart__summary-label">{summaryLabels.topLabel}</span>
          <span className="sales-by-channel-chart__summary-value">
            {model.rows[0]?.label ?? "—"}
          </span>
          <span className="sales-by-channel-chart__summary-hint">{topHintResolved}</span>
        </div>
      </section>
      ) : null}

      <figure
        className="sales-by-channel-chart__plot"
        style={plotStyle}
        aria-label={summaryLabels.ariaLabel}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={model.rows}
            margin={mirrorChartMargin(TREND_CHART_MARGIN, isRTL)}
            barCategoryGap={10}
            barGap={4}
          >
            <CartesianGrid
              horizontal={false}
              vertical
              strokeDasharray="4 6"
              stroke="#e2e8f0"
            />
            <XAxis
              type="number"
              domain={[0, "dataMax"]}
              reversed={chartRtl.axisReversed}
              tickFormatter={v => formatChartValue(Number(v))}
              tick={{ fontSize: 11, fill: "#64748b", fontWeight: 500 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={yAxisLabelWidth}
              orientation={chartRtl.yAxisOrientation}
              tickMargin={4}
              tick={{ fontSize: 12, fill: "#334155", fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <Tooltip
              content={(
                <SalesByChannelTooltip
                  tooltipMetricLabel={summaryLabels.tooltipMetricLabel}
                  tooltipShareQualifier={summaryLabels.tooltipShareQualifier}
                  valueKind={valueKind}
                />
              )}
              cursor={{ fill: "rgba(37, 99, 235, 0.06)" }}
              wrapperStyle={{ outline: "none" }}
            />
            <Bar
              dataKey="value"
              name={summaryLabels.tooltipMetricLabel}
              radius={isRTL ? [8, 0, 0, 8] : [0, 8, 8, 0]}
              maxBarSize={26}
              background={{ fill: "hsl(220 16% 94%)", radius: 8 }}
              activeBar={false}
              isAnimationActive
            >
              {model.rows.map((row, index) => (
                <Cell
                  key={`${row.label}-${index}`}
                  fill={channelBarFill(Math.max(0, row.rank - 1))}
                />
              ))}
              <LabelList
                dataKey="value"
                position={chartRtl.labelPosition}
                offset={10}
                formatter={(v: number | string, _entry: unknown, index?: number) => {
                  const row = typeof index === "number" ? model.rows[index] : undefined;
                  if (valueKind === "currency" && row?.formattedValue) {
                    return row.formattedValue;
                  }
                  return formatChartValue(typeof v === "number" ? v : Number(v));
                }}
                style={{
                  fill: "#475569",
                  fontSize: 11,
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </figure>
    </div>
  );
};
